import { useId } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';

import { toggleBtnSx, toggleGroupSx } from './analyticsUi';

export interface AnalyticsTab<T extends string> {
    value: T;
    label: string;
    disabled?: boolean;
}

interface AnalyticsTabsProps<T extends string> {
    tabs: AnalyticsTab<T>[];
    selected: T;
    onChange: (value: T) => void;
    ariaLabel: string;
    /** Hide the control when only one option is available. */
    hideSingle?: boolean;
    sx?: SxProps<Theme>;
}

const AnalyticsTabs = <T extends string>({
    tabs,
    selected,
    onChange,
    ariaLabel,
    hideSingle = false,
    sx,
}: AnalyticsTabsProps<T>) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    const labelId = useId();
    const rootSx = [{ mb: 2 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])];

    if (hideSingle && tabs.length <= 1) return null;

    if (isMobile) {
        return (
            <FormControl fullWidth size="small" sx={rootSx}>
                <InputLabel id={labelId}>{ariaLabel}</InputLabel>
                <Select<T>
                    labelId={labelId}
                    label={ariaLabel}
                    value={selected}
                    onChange={(event: SelectChangeEvent<T>) => onChange(event.target.value as T)}
                >
                    {tabs.map((tab) => (
                        <MenuItem key={tab.value} value={tab.value} disabled={tab.disabled}>
                            {tab.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    return (
        <Box role="tablist" aria-label={ariaLabel} sx={[toggleGroupSx, ...rootSx]}>
            {tabs.map((tab) => (
                <Box
                    component="button"
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={tab.value === selected}
                    disabled={tab.disabled}
                    sx={[
                        toggleBtnSx(tab.value === selected),
                        {
                            flex: {
                                xs: tabs.length > 1 ? '1 1 calc(50% - 4px)' : '1 1 100%',
                                sm: '0 0 auto',
                            },
                            minHeight: { xs: 40, sm: 36 },
                            whiteSpace: { xs: 'normal', sm: 'nowrap' },
                            textAlign: 'center',
                            lineHeight: 1.2,
                        },
                    ]}
                    onClick={() => onChange(tab.value)}
                >
                    {tab.label}
                </Box>
            ))}
        </Box>
    );
};

export default AnalyticsTabs;
